import { useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { Maximize2, MessageSquare, Minimize2, X } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/toast';
import { useMe } from '@/features/auth/useMe';
import { canAuthorCoachFeedback } from '@/features/coach/coachWorkspaceAccess';
import { WorkspaceHeader, WorkspaceHeaderProvider } from '@/features/coach/components/WorkspaceHeader';
import { matchLabel } from '@/features/coach-review/components/MatchSlotPicker';
import { useCanonicalCoachWorkspace } from '@/features/feedback-integration/CanonicalCoachWorkspaceHost';
import { SLOT_LABELS } from '@/features/matches/matchSlots';
import { useMatches } from '@/features/matches/useMatches';
import { getPublicNavCapabilities } from '@/features/navigation/navCapabilities';
import { TeamScopeGate } from '@/features/navigation/TeamScopeGate';
import { createResourceDeletion } from '@/lib/resourceDeletion';
import { useScopeStore } from '@/stores/scope';
import type { ResourceKind } from '@/lib/types';
import { ResourceDetailsPanel } from './ResourceDetailsPanel';
import { RESOURCE_KIND_ICON, RESOURCE_KIND_LABEL, ResourceKindChooser } from './ResourceKindChooser';
import { ResourceStageBoard } from './ResourceStageBoard';
import { ResourceStageClip } from './ResourceStageClip';
import { ResourceStageMoment } from './ResourceStageMoment';
import type { ResourceBoardSession } from './resourceEditorSession';
import type { ResourceVideoSession, ResourceVideoValue } from './resourceVideoSession';
import type { ResourceStagePosition } from './ResourceVideoStage';
import { resourceEditorHref } from './resourceEditorLinks';
import type { ResourceEditorLinkTarget } from './resourceEditorSessionCore';
import { ResourceReturnError } from './resourceText';
import { useResourceEditor, type ResourceEditorLocation, type ResourceEditorRoute, type ResourceEditorSlot } from './useResourceEditor';

const KINDS:ResourceKind[] = ['moment', 'clip', 'tactical_board'];
const LIBRARY = { to:'/coach' as const, search:{ tab:'library' as const, library_kind:'resources' as const } };
const EDITOR_PATH = /^\/coach\/resources\//;
/** The kind label in running text ("this tactical board"). */
const kindName = (kind:ResourceKind) => RESOURCE_KIND_LABEL[kind].toLowerCase();

/** One editor per Resource kind, reachable by URL (plan PR 2). The details
 * panel and the stage each mount once; Focus only changes their layout. */
export function ResourceEditorPage({ route }:{ route:ResourceEditorRoute }) {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const selectionRequired = useScopeStore(state => state.selectionRequired);
  const canOpenCoach = getPublicNavCapabilities(me.data).canOpenCoach;
  const canAuthor = canOpenCoach && canAuthorCoachFeedback(me.data);
  const workspace = useCanonicalCoachWorkspace();
  const matches = useMatches();
  const go = (location:ResourceEditorLocation) => void navigate(resourceEditorHref(location, true));
  const editor = useResourceEditor(route, go);
  const { phase, session, state, readOnly, mounted, context } = editor;
  const [focus, setFocus] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [switching, setSwitching] = useState<{ next:ResourceKind; replaced:ResourceKind } | null>(null);
  const [choosing, setChoosing] = useState<ResourceKind | null>(null);
  // F2: unsaved edits elsewhere on the page when returning to the draft.
  const [asking, setAsking] = useState<{ kinds:ResourceKind[]; answer(choice:'save' | 'discard' | null):void } | null>(null);
  const position = useRef<ResourceStagePosition>({ playhead:0, duration:0 });
  const deletion = useRef<{ resourceId:string; command:ReturnType<typeof createResourceDeletion> } | null>(null);
  const authoringActive = !!workspace?.authoringActive;
  // Leaving with unsaved edits, shown or parked behind a kind tab, asks first:
  // in-app links, Back, reload and tab close. Only this page's own entries
  // (its /new → /$id replace, a kind tab) keep the sessions; any other editor
  // address (the Review tab, another Resource) would release them.
  // Read live: saving a parked session does not re-render the shown one.
  const live = useRef(editor); live.current = editor;
  const unsaved = () => live.current.unsaved().length > 0;
  const leaving = useBlocker({ withResolver:true, enableBeforeUnload:unsaved,
    shouldBlockFn:({ next }) => unsaved()
      && !(EDITOR_PATH.test(next.pathname) && live.current.ownsEntry((next.search as { entry?:string }).entry)) });

  // Same role gate as Coach Review: a non-author lands on Matches or the hub.
  const roleRedirect = useRef(false);
  useEffect(() => {
    if (!me.data?.user || canAuthor || roleRedirect.current) return;
    roleRedirect.current = true;
    void navigate(canOpenCoach ? { to:'/coach', search:{ tab:'matches' }, replace:true } : { to:'/hub', replace:true });
  }, [me.data, canAuthor, canOpenCoach, navigate]);

  // The feedback composer opens above this page; its session waits meanwhile.
  useEffect(() => {
    if (session && session.getState().status !== 'unavailable') session.setActive(!authoringActive);
  }, [session, authoringActive]);
  useEffect(() => {
    if (!focus) return;
    const keys = (event:KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('[data-coach-modal-active="true"]')) return;
      if (detailsOpen) setDetailsOpen(false); else setFocus(false);
    };
    window.addEventListener('keydown', keys);
    return () => window.removeEventListener('keydown', keys);
  }, [focus, detailsOpen]);

  const exit = () => void navigate(LIBRARY);
  const header = <WorkspaceHeader active="library"/>;
  if (phase.status !== 'ready' || !session || !state || !mounted || !context) {
    return <WorkspaceHeaderProvider><main className="coach-review-page resource-editor-page text-ink">
      {header}
      {selectionRequired ? <TeamScopeGate/> : me.data?.user && !canAuthor ? null : phase.status === 'choose'
        ? <ResourceKindChooser kind={phase.kind} matchId={route.mode === 'new' ? route.matchId : undefined} onClose={exit}
          onOpen={(kind, matchId, slot) => void navigate(resourceEditorHref({ mode:'new', kind, matchId, slot,
            entry:route.entry ?? crypto.randomUUID() }, true))}/>
        : phase.status === 'error'
          ? <div className="flex max-w-xl flex-col gap-3 rounded-panel border border-rule bg-surface p-[18px]" role="alert" data-testid="resource-editor-unavailable">
            <h2>{phase.reason === 'unavailable' ? 'This Resource is not available' : 'This Resource editor could not open'}</h2>
            <p className="text-sm text-ink-muted">{phase.reason === 'unavailable' ? 'It may be another coach’s draft, which only they can see.'
              : 'Your saved work is kept. Try again from the library.'}</p>
            <div><Button type="button" variant="outline" onClick={exit}>Back to library</Button></div>
          </div>
          : <p role="status" className="text-sm text-ink-muted">Opening Resource…</p>}
    </main></WorkspaceHeaderProvider>;
  }

  const kind = mounted.kind;
  const available = state.status === 'available' ? state : null;
  const video = available && 'recording' in available.value ? available.value as ResourceVideoValue : null;
  const match = video?.recording ? matches.data?.find(row => row.id === video.recording?.match_id) : undefined;
  const matchName = match ? matchLabel(match) : 'Match recording';
  const eyebrow = kind === 'tactical_board' ? 'Resource · Tactical board'
    : ['Resource', match ? `${match.home_team} vs ${match.away_team}` : null, video?.recording ? SLOT_LABELS[video.recording.slot] : null].filter(Boolean).join(' · ');
  const saved = !!available?.origin;
  const title = readOnly ? RESOURCE_KIND_LABEL[kind] : `${saved ? 'Edit' : 'New'} ${RESOURCE_KIND_LABEL[kind].toLowerCase()}`;
  const editable = !!available?.canEdit && !readOnly;
  const active = !authoringActive;

  const switchTo = async (next:ResourceKind, choice?:'save' | 'discard', pick?:{ matchId:string; slot:ResourceEditorSlot }) => {
    const replaced = editor.replacing(next);
    try {
      const result = await editor.switchKind(next, position.current, choice, pick);
      setSwitching(result === 'confirm' && replaced ? { next, replaced } : null);
      setChoosing(result === 'choose' ? next : null);
      if (result === 'switched' && choice === 'save') toast.success('Saved as a draft', { description:'It stays in your library, visible only to you.' });
    } catch { setSwitching(null); toast.error('The draft could not be saved. Your edits are kept.'); }
  };
  /** F2 steps 3–5: back to the feedback draft with every Resource this page
   * saved for it pinned, then to the page the draft was written on. Unsaved
   * edits are saved or discarded first, asking when `choice` is not given. */
  const returnToDraft = async (choice?:'save' | 'discard') => {
    const host = workspace?.authoring.current, target = editor.target;
    if (!host || target.kind !== 'feedback') throw new ResourceReturnError('unavailable');
    const pending = editor.unsaved();
    const decision = choice ?? (pending.length ? await new Promise<'save' | 'discard' | null>(answer =>
      setAsking({ kinds:pending.map(kept => kept.kind), answer:value => { setAsking(null); answer(value); } })) : null);
    if (pending.length && !decision) throw new ResourceReturnError('cancelled');
    if (decision === 'save') await editor.saveUnsaved();
    else if (decision === 'discard') editor.discardUnsaved();
    const pins = editor.unpinned().length > 0;
    try {
      await new Promise<void>((resolve, reject) => {
        const ready = (destination:ResourceEditorLinkTarget) => void editor.pinAll(destination)
          .then(refused => refused ? reject(new ResourceReturnError(refused.reason)) : resolve(), reject);
        if (!host.activateEntry(target.entry_id, pins ? ready : undefined, () => reject(new ResourceReturnError('unavailable'))))
          reject(new ResourceReturnError('unavailable'));
        else if (!pins) resolve();
      });
    } catch (error) {
      // A refusal is explained here, not behind the composer that just opened.
      host.suspend();
      throw error instanceof ResourceReturnError ? error : new ResourceReturnError('link-failed');
    }
    workspace?.back(target.entry_id);
  };
  const giveFeedback = async () => {
    const host = workspace?.authoring.current;
    if (!host) throw new Error('feedback_unavailable');
    // Read afresh: Save draft and Publish call this right after their own save.
    const before = session.getState();
    const edited = before.status === 'available' && before.canEdit && !readOnly;
    if (editor.target.kind === 'feedback') {
      // Back to feedback is Save draft's return; an untouched new Resource
      // returns without creating anything.
      if (edited && before.dirty) await editor.save();
      return returnToDraft();
    }
    if (edited && (!before.origin || before.dirty)) await editor.save();
    const now = session.getState();
    if (now.status !== 'available' || !now.origin) throw new Error('resource_unsaved');
    // F1 step 7: a new feedback draft with this exact version pinned.
    const pin = { resource_id:now.origin.resource_id, revision_id:now.origin.revision_id };
    await new Promise<void>((resolve, reject) => host.startEntry(crypto.randomUUID(),
      destination => void destination.admitPin({ kind, pin })
        .then(outcome => outcome.status === 'retained' ? reject(new ResourceReturnError(outcome.reason)) : resolve(), reject),
      () => reject(new Error('feedback_cancelled'))));
  };
  /** Close: inside a feedback draft, back to it; otherwise the library. */
  const close = () => {
    if (editor.target.kind !== 'feedback' || !workspace?.authoring.current) { exit(); return; }
    void returnToDraft().catch(error => {
      if (error instanceof ResourceReturnError && error.message) toast.error(error.message);
    });
  };
  /** One delete operation per Resource, resent unchanged after an unknown result. */
  const remove = async () => {
    const resource = available?.authority?.resource;
    if (!resource) throw new Error('This Resource is unavailable.');
    if (deletion.current?.resourceId !== resource.resource_id) {
      deletion.current = { resourceId:resource.resource_id, command:createResourceDeletion(context, resource.resource_id, resource.version) };
    }
    const outcome = await deletion.current.command.send();
    if (outcome.status === 'deleted' || outcome.status === 'gone') {
      toast.success(outcome.status === 'deleted' ? 'Resource deleted' : 'This Resource was already deleted');
      exit(); return;
    }
    if (outcome.status === 'stale' && outcome.currentVersion !== null) deletion.current.command.rebase(outcome.currentVersion);
    throw new Error(outcome.status === 'stale' ? outcome.currentVersion === null ? 'This Resource changed. Reopen it and try again.'
      : 'This Resource changed. Choose Delete again to confirm.'
      : outcome.status === 'rejected' ? outcome.code === 'resource_in_use'
        ? 'This Resource is still used by feedback, so it cannot be deleted.' : 'This Resource could not be deleted.'
        : outcome.status === 'unknown' ? 'The delete was not confirmed. Try again; it resends the same request.'
          : 'Your team or season changed. Reopen this Resource to try again.');
  };
  const openCurrent = () => available?.origin && void navigate({ to:'/coach/resources/$resourceId',
    params:{ resourceId:available.origin.resource_id } });

  const stage = !available ? <p role="status" className="text-sm text-ink-muted">This Resource is unavailable.</p>
    : kind === 'tactical_board'
      ? <ResourceStageBoard key={mounted.entryId} session={session as ResourceBoardSession} state={available as Extract<ReturnType<ResourceBoardSession['getState']>, { status:'available' }>}
        editable={editable && active} focus={focus} players={workspace?.players ?? []}/>
      : kind === 'moment'
        ? <ResourceStageMoment key={mounted.entryId} session={session as ResourceVideoSession} state={available as Extract<ReturnType<ResourceVideoSession['getState']>, { status:'available' }>}
          context={context} editable={editable} active={active} focus={focus} matchName={matchName} position={position} setFlush={editor.setFlush}/>
        : <ResourceStageClip key={mounted.entryId} session={session as ResourceVideoSession} state={available as Extract<ReturnType<ResourceVideoSession['getState']>, { status:'available' }>}
          context={context} editable={editable} active={active} focus={focus} matchName={matchName} position={position}/>;

  return <WorkspaceHeaderProvider><main className="coach-review-page resource-editor-page text-ink" data-testid="resource-editor"
    data-kind={kind} data-focus={focus ? 'true' : 'false'}>
    {header}
    <div className="mb-5 mt-2 grid items-center gap-x-5 gap-y-4 grid-cols-[minmax(0,1fr)_auto] min-[1100px]:grid-cols-[minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <p className="eyebrow" data-testid="resource-editor-eyebrow">{eyebrow}</p>
        <h1 data-testid="resource-editor-title">{title}</h1>
      </div>
      <div className="col-span-full min-[1100px]:col-span-1 min-[1100px]:col-start-2 min-[1100px]:row-start-1">
        <div role="tablist" aria-label="Resource kind" className="inline-flex max-w-full flex-wrap rounded-pill bg-elevated p-1">
          {KINDS.map(option => {
            const Icon = RESOURCE_KIND_ICON[option], selected = option === kind, locked = saved && !selected;
            return <button key={option} type="button" role="tab" aria-selected={selected}
              disabled={!selected && (locked || !!readOnly || !editor.canSwitch(option))}
              title={locked ? 'The kind of a saved Resource cannot change'
                : !selected && !readOnly && !editor.canSwitch(option) ? 'Wait until the save is confirmed' : undefined}
              data-testid={`resource-tab-${option}`} onClick={() => void switchTo(option)}
              className={`inline-flex min-h-10 items-center gap-2 px-3.5 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                selected ? 'rounded-pill bg-accent text-accent-ink' : 'rounded-control text-ink-muted hover:text-ink'}`}>
              <Icon size={16} aria-hidden/>{RESOURCE_KIND_LABEL[option]}</button>;
          })}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2 min-[1100px]:col-start-3 min-[1100px]:row-start-1 row-start-1 col-start-2">
        <Button type="button" variant="outline" onClick={() => setFocus(true)} data-testid="resource-focus"><Maximize2 aria-hidden/>Focus</Button>
        <Button type="button" variant="ghost" onClick={close} data-testid="resource-close"><X aria-hidden/>Close</Button>
      </div>
    </div>

    <div className="grid items-start gap-5 min-[980px]:grid-cols-[minmax(0,1fr)_340px]">
      <section aria-label="Stage" data-testid="resource-editor-stage"
        className={focus ? 'fixed inset-0 z-[80] flex flex-col gap-3 overflow-y-auto bg-navy-900 px-6 pb-5 pt-4 text-paper' : 'min-w-0'}>
        {focus && <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1"><p className="eyebrow text-paper/70">{eyebrow}</p><h2 className="text-xl text-paper">{video?.label || available?.value.label || title}</h2></div>
          <Button type="button" variant="outline" className="border-navy-600 text-paper hover:bg-navy-700" onClick={() => setFocus(false)} data-testid="resource-focus-exit">
            <Minimize2 aria-hidden/>Exit focus</Button>
        </div>}
        {stage}
        {focus && <div className="flex justify-end">
          <Button type="button" aria-expanded={detailsOpen} aria-controls="resource-details" onClick={() => setDetailsOpen(open => !open)} data-testid="resource-details-toggle">
            <MessageSquare aria-hidden/>{detailsOpen ? 'Hide details' : 'Details'}</Button>
        </div>}
      </section>
      <aside id="resource-details" aria-label="Resource details" data-open={detailsOpen ? 'true' : 'false'} data-testid="resource-details"
        className={focus ? `fixed inset-y-0 right-0 z-[90] w-[min(380px,100%)] overflow-y-auto bg-canvas p-5 shadow-pop transition-[transform,visibility] duration-200 ${detailsOpen ? '' : 'invisible translate-x-full'}`
          : 'min-[980px]:sticky min-[980px]:top-4'}>
        {focus && <div className="mb-3 flex items-center"><h3 className="flex-1">Details</h3>
          <Button type="button" variant="ghost" size="icon" aria-label="Hide details" onClick={() => setDetailsOpen(false)}><X aria-hidden/></Button></div>}
        <ResourceDetailsPanel editor={editor} onGiveFeedback={giveFeedback} onOpenCurrent={openCurrent} onDelete={remove}
          pinnedElsewhere={route.mode === 'saved' && !!route.revision}
          onReturnToFeedback={editor.target.kind === 'feedback' ? () => returnToDraft() : undefined}/>
      </aside>
    </div>

    {leaving.status === 'blocked' && <ConfirmDialog title={`Keep this ${kindName(kind)} as a draft?`}
      message={editor.target.kind === 'feedback'
        ? 'You have unsaved changes. Save draft pins them to your feedback draft and takes you back there.'
        : 'You have unsaved changes. A draft stays in your library, visible only to you, until you publish or archive it.'}
      confirmLabel="Save draft" cancelLabel="Cancel" onClose={leaving.reset}
      onConfirm={() => {
        if (editor.target.kind === 'feedback') {
          // Save draft's own return replaces the navigation that was asked for.
          leaving.reset();
          void returnToDraft('save').catch(error => toast.error(error instanceof ResourceReturnError && error.message
            ? error.message : 'The draft could not be saved. Your edits are kept.'));
          return;
        }
        void editor.saveUnsaved().then(leaving.proceed, () => {
          leaving.reset(); toast.error('The draft could not be saved. Your edits are kept.');
        });
      }}
      altAction={{ label:'Discard', danger:true, 'data-testid':'resource-leave-discard', onClick:() => {
        editor.discardUnsaved();
        leaving.proceed();
      } }}/>}
    {asking && <ConfirmDialog title={`Keep your unsaved ${asking.kinds.map(kindName).join(' and ')} as a draft?`}
      message="Save draft pins it to your feedback draft too. Discard drops those changes." confirmLabel="Save draft" cancelLabel="Cancel"
      onClose={() => asking.answer(null)} onConfirm={() => asking.answer('save')}
      altAction={{ label:'Discard', danger:true, 'data-testid':'resource-return-discard', onClick:() => asking.answer('discard') }}/>}
    {switching && <ConfirmDialog title={`Keep the unsaved ${kindName(switching.replaced)} as a draft?`}
      message={`A ${kindName(switching.next)} starts a separate Resource on the same recording.${editor.target.kind === 'feedback' ? ' Both are pinned to your feedback draft.' : ''}`}
      confirmLabel="Save draft, then switch" cancelLabel="Cancel" onClose={() => setSwitching(null)}
      onConfirm={() => void switchTo(switching.next, 'save')}
      altAction={{ label:'Discard', danger:true, 'data-testid':'resource-switch-discard', onClick:() => void switchTo(switching.next, 'discard') }}/>}
    {choosing && <ResourceKindChooser kind={choosing} note="Pick the recording for this resource." onClose={() => setChoosing(null)}
      onOpen={(next, matchId, slot) => { setChoosing(null); void switchTo(next, undefined, matchId && slot ? { matchId, slot } : undefined); }}/>}
  </main></WorkspaceHeaderProvider>;
}
